import { useEffect, useState } from "react";
import { IDatabaseLineItem } from "../types";
import { IXactimateToolRowData } from "../../../../shared/common/Azure";
import { toastNotify } from "../../../../shared/client/FrontendUtils";
import RestAPI from "../../../../utils/restAPI";
import { fetchXactimateMappingsForLineItem } from "../../../../utils/xactimateMappingsAPI";
import TopMostPortal from "../../../../components/ui/utils/Portals";
import { MappingsSection } from "./XactimateSyncReview/MappingsSection";

interface Props {
	lineItem: IDatabaseLineItem;
	onClose: () => void;
	onConfirmed: () => Promise<void>;
}

type Status = "loading" | "ready" | "deleting";

export const DeleteLineItemDialog = ({ lineItem, onClose, onConfirmed }: Props) => {
	const [status, setStatus] = useState<Status>("loading");
	const [mappings, setMappings] = useState<IXactimateToolRowData[]>([]);
	const [mappingsCollapsed, setMappingsCollapsed] = useState(true);

	useEffect(() => {
		const fetchMappings = async () => {
			const result = await fetchXactimateMappingsForLineItem(lineItem.id);
			setMappings(result);
			setStatus("ready");
		};
		fetchMappings().catch(() => {
			toastNotify("Error loading Xactimate mappings", true);
			onClose();
		});
	}, [lineItem.id]);

	const handleConfirm = async () => {
		setStatus("deleting");
		try {
			for (const mapping of mappings) {
				const res = await RestAPI.fetchRebuildAPI(`/rest/tools/xactimateMappings/${mapping.id}`, "DELETE");
				if (res !== null) {
					throw new Error("Failed to delete mapping");
				}
			}
			await onConfirmed();
			onClose();
		} catch {
			toastNotify("Error deleting Xactimate mappings", true);
			setStatus("ready");
		}
	};

	const mappingCount = mappings.length;
	const busy = status === "loading" || status === "deleting";

	return (
		<TopMostPortal>
			<div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
				<div className="bg-white border border-gray-200 rounded-lg max-w-2xl w-full shadow-lg max-h-[90vh] flex flex-col">
					<div className="px-5 py-4 border-b border-gray-200">
						<h2 className="text-lg font-semibold text-gray-900">Delete line item?</h2>
					</div>

					<div className="px-5 py-4 space-y-3 text-sm text-gray-700 overflow-y-auto">
						{status === "loading" && <p>Loading Xactimate mappings…</p>}
						{status !== "loading" && (
							<>
								<p>Are you sure you want to delete this line item?</p>
								{mappingCount > 0 ? (
									<>
										<p>
											This will delete{" "}
											<span className="font-semibold">
												{mappingCount} Xactimate mapping{mappingCount === 1 ? "" : "s"}
											</span>{" "}
											associated with this line item.
										</p>
										<MappingsSection
											lineItem={lineItem}
											mappings={mappings}
											collapsed={mappingsCollapsed}
											onToggle={() => setMappingsCollapsed((v) => !v)}
											label="Xactimate Mappings"
											variant="bad"
										/>
									</>
								) : (
									<p className="text-gray-500">This line item has no Xactimate mappings.</p>
								)}
							</>
						)}
					</div>

					<div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
						<button
							onClick={onClose}
							disabled={status === "deleting"}
							className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md transition-colors disabled:opacity-50"
						>
							Cancel
						</button>
						<button
							onClick={handleConfirm}
							disabled={busy}
							className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-500 text-white rounded-md transition-colors disabled:opacity-50"
						>
							{status === "deleting" ? "Deleting…" : "Delete"}
						</button>
					</div>
				</div>
			</div>
		</TopMostPortal>
	);
};
